import { Button, FormGroup, FormParagraph, FormSection, Heading, Spacer, Text, TextInput } from '@ksyos/design-system';
import IBAN from 'iban';
import { useState } from 'react';
import { BankAccountEntity } from '@common/enums/bankAccountEntity';
import type { ValidationErrors } from '../../../utils/validation';
import { requestBankAccountDetailsChangeSchema } from '../../../../common/validation/bankAccountDetails';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { Modal } from '../../elements/modals/Modal';

interface IRequestBankAccountDetailsChangeModalProps {
    bankAccountEntity: BankAccountEntity;
    currentBankAccountNumber?: string;
    currentSubscriber?: string;
    entityId: string;
    isOpen: boolean;
    onClose: () => void;
}

interface IRequestBankAccountDetailsChange {
    bankAccountNumber: string | null;
    subscriber: string | null;
}

export function RequestBankAccountDetailsChangeModal(props: IRequestBankAccountDetailsChangeModalProps) {
    const [requestBankAccountDetailsChange, setRequestBankAccountDetailsChange] =
        useState<IRequestBankAccountDetailsChange>({
            bankAccountNumber: null,
            subscriber: null,
        });
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);

    const actionbuttons = [
        <Button
            onClick={props.onClose}
            variant="outline"
        >
            {translateKey('bankAccountDetails.cancel')}
        </Button>,
        <Button onClick={async () => await handleSubmit()}>{translateKey('bankAccountDetails.submit')}</Button>,
    ];

    return (
        <Modal
            actionButtons={!showConfirmationMessage ? actionbuttons : []}
            isOpen={props.isOpen}
            onClose={props.onClose}
            size="large"
            title={translateKey('bankAccountDetails.requestChange')}
        >
            {!showConfirmationMessage && (
                <>
                    <Text mb={10}>
                        {props.bankAccountEntity === BankAccountEntity.employment
                            ? translateKey('bankAccountDetails.employmentExplanation')
                            : translateKey('bankAccountDetails.organizationExplanation')}
                    </Text>

                    <FormSection title={translateKey('bankAccountDetails.current')}>
                        <FormGroup label={translateKey('finance.toIban')}>
                            <FormParagraph>
                                {props.currentBankAccountNumber
                                    ? IBAN.printFormat(props.currentBankAccountNumber)
                                    : translateKey('common.unknown')}
                            </FormParagraph>
                        </FormGroup>
                        <FormGroup label={translateKey('finance.inNameOf')}>
                            <FormParagraph>{props.currentSubscriber ?? translateKey('common.unknown')}</FormParagraph>
                        </FormGroup>
                    </FormSection>

                    <FormSection title={translateKey('bankAccountDetails.new')}>
                        <FormGroup
                            hasErrors={errors.has('bankAccountNumber')}
                            isRequired
                            label={translateKey('finance.toIban')}
                        >
                            <TextInput
                                error={
                                    errors.get('bankAccountNumber') &&
                                    translateKey(`validation.${errors.get('bankAccountNumber')}`)
                                }
                                onChange={(e) => {
                                    const formattedBankAccountNumber = e.target.value
                                        .replace(/[^\w]/g, '')
                                        .replace(/(.{4})/g, '$1 ')
                                        .trim();
                                    handleChange('bankAccountNumber', formattedBankAccountNumber);
                                }}
                                placeholder={translateKey('bankAccountDetails.enterBankAccountNumberPlaceholder')}
                                value={
                                    requestBankAccountDetailsChange.bankAccountNumber
                                        ? IBAN.printFormat(requestBankAccountDetailsChange.bankAccountNumber)
                                        : ''
                                }
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('subscriber')}
                            isRequired
                            label={translateKey('finance.inNameOf')}
                        >
                            <TextInput
                                error={errors.get('subscriber') && translateKey('validation.any_empty')}
                                onChange={(e) => handleChange('subscriber', e.target.value)}
                                placeholder={translateKey('bankAccountDetails.enterBankAccountNamePlaceholder')}
                                value={requestBankAccountDetailsChange.subscriber ?? ''}
                            />
                        </FormGroup>
                    </FormSection>
                </>
            )}

            {showConfirmationMessage && (
                <Spacer spacing="large">
                    <Heading size="small">{translateKey('bankAccountDetails.successModalTitle')}</Heading>
                    <Text>{translateKey('bankAccountDetails.dataIsProcessedInAFewDays')}</Text>
                    <Text>
                        {props.bankAccountEntity === BankAccountEntity.employment
                            ? translateKey('bankAccountDetails.employmentFollowStatusExplanation')
                            : translateKey('bankAccountDetails.organizationFollowStatusExplanation')}
                    </Text>
                </Spacer>
            )}
        </Modal>
    );

    function handleChange<K extends keyof IRequestBankAccountDetailsChange>(
        name: K,
        value: IRequestBankAccountDetailsChange[K]
    ) {
        setRequestBankAccountDetailsChange({
            ...requestBankAccountDetailsChange,
            [name]: value,
        } as IRequestBankAccountDetailsChange);
    }

    function validateBankAccountDetails() {
        const validationResult = validate(requestBankAccountDetailsChangeSchema, requestBankAccountDetailsChange);

        if (
            requestBankAccountDetailsChange.bankAccountNumber &&
            !IBAN.isValid(requestBankAccountDetailsChange.bankAccountNumber)
        ) {
            validationResult.errors = validationResult.errors.set('bankAccountNumber', 'not_valid_iban');
        }

        return validationResult.errors;
    }

    async function handleSubmit() {
        if (!isSaving) {
            setIsSaving(true);
            const validationErrors = validateBankAccountDetails();
            setErrors(validationErrors);

            if (validationErrors.size === 0 && requestBankAccountDetailsChange.bankAccountNumber) {
                const result = await fetchJson('/api/bankAccountDetails/requestChange', 'POST', {
                    entityId: props.entityId,
                    bankAccountEntity: props.bankAccountEntity,
                    bankAccountDetails: {
                        subscriber: requestBankAccountDetailsChange.subscriber?.trim(),
                        bankAccountNumber: IBAN.electronicFormat(requestBankAccountDetailsChange.bankAccountNumber),
                    },
                });
                if (result.status === 200) {
                    setShowConfirmationMessage(true);
                }
            }

            setIsSaving(false);
        }
    }
}
